import { createSlice } from '@reduxjs/toolkit';
import { createDataSources, dataSourceHistoryByPage, deleteDataSources, getDataSourceById, getDataSourcesList, updateDataSourceById } from '../thunks/dataSourcesThunk';
import { toast } from 'react-toastify';
import { getHcpProfileData, getImportance, getIntensity, getPhysiciansList, postImportance, postIntensity } from '../thunks/physiciansThunk';


const physicianInitialState ={
    error: null,
    isLoading: true,
    dualDeciles: [],
    decileAndSegmentData: {},
    totalDecilesQuantity: 0,
    totalSegmentsQuantity: 0,
    totalDualDecilesQuantity: 0,
    physicianData: [],
    totalPhysicians: 0,
    totalPages: 0,
    pageSize: 50,
    hcpProfileData: {},
    isHcpProfileLoading: false,
    selectedNPI: 0
};
const physicianSlice = createSlice({
    name: 'physician',
    initialState: physicianInitialState,
    reducers: {
        resetToInitialState(state, action) {
            Object.assign(state, physicianInitialState);
        },
        handleImportanceChange(state, action) {
            const {
                row, selected, dataType 
            } = action.payload;

            const updatedData = state.decileAndSegmentData[dataType].map(item => {
                if (item.id === row.id) {
                    return {
                        ...item,
                        importance: selected.value
                    };
                }
                return item;
            });

            return {
                ...state,
                decileAndSegmentData: {
                    ...state.decileAndSegmentData,
                    [dataType]: updatedData
                }
            };
        },
        handleIntensityChange(state, action) {
            const {
                row, selected, field 
            } = action.payload;
    
            const updatedData = state.dualDeciles.map(item => {
                if (item.id === row.id) {
                    return {
                        ...item,
                        [field]: selected.value
                    };
                }
                return item;
            });
    
            return {
                ...state,
                dualDeciles: updatedData
            };
        } , 
        setSelectedNPI(state, action) {
            const { npiId } = action.payload;
            return { ...state, selectedNPI: npiId };
        }

    },
    extraReducers: (builder) => {
        builder
            .addCase(getImportance.fulfilled, (state,action) => {
                state.decileAndSegmentData = action.payload?.result;
                state.totalDecilesQuantity = calculateTotal(action.payload?.result?.decile || []);
                state.totalSegmentsQuantity = calculateTotal(action.payload?.result?.segment || []);
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getImportance.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getImportance.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                toast.error(action.payload);
            })
            .addCase(getIntensity.fulfilled, (state,action) => {
                state.dualDeciles = action.payload?.result;
                state.totalDualDecilesQuantity = calculateTotal(action.payload?.result || []);
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getIntensity.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getIntensity.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                toast.error(action.payload);
            })
            .addCase(postImportance.fulfilled, (state,action) => {
                state.error = null;
                state.isLoading = false;
                if(action.payload.status_code===200){
                    toast.success(action.payload.status);
                } else{
                    toast.error(action.payload.status); 
                }
            })
            .addCase(postImportance.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(postImportance.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                toast.error(action.payload);
            })
            .addCase(postIntensity.fulfilled, (state,action) => {
                state.error = null;
                state.isLoading = false;
                if(action?.payload?.status_code===200){
                    toast.success(action?.payload?.status);
                } else{
                    toast.error(action?.payload?.status); 
                }
            })
            .addCase(postIntensity.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(postIntensity.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                toast.error(action.payload);
            })
            .addCase(getPhysiciansList.fulfilled, (state,action) => {
                const { meta } = action;
                const { type } = meta.arg;
                if(type !== 'download'){
                    state.physicianData = physicianTableData(action.payload?.results?.physicians || []);
                    state.totalPages = Math.ceil((action.payload?.results?.total || 0) / state.pageSize);
                    state.totalPhysicians = action.payload?.results?.total || 0;
                }
                state.error = null;
                state.isLoading = false;
            
            })
            .addCase(getPhysiciansList.pending, (state, action) => {
                const { meta } = action;
                const { type } = meta.arg;
                if(type !== 'download'){
                    state.isLoading = true;
                    state.error = null;
                }
            
            })
            .addCase(getPhysiciansList.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                toast.error(action.payload);
            })
            .addCase(getHcpProfileData.fulfilled, (state,action) => {
                state.error = null;
                state.hcpProfileData = action?.payload?.result?.data[0] || {};
                state.isHcpProfileLoading = false;
            })
            .addCase(getHcpProfileData.pending, (state, action) => {
                state.isHcpProfileLoading = true;
                state.error = null;
            })
            .addCase(getHcpProfileData.rejected, (state, action) => {
                state.isHcpProfileLoading = false;
                state.error = action.payload;
                toast.error(action.payload);
            });
    }

});

const calculateTotal = (data) =>data.reduce((total, acc)=>total + (acc?.physician_qty || 0), 0);

const physicianTableData = (data) => {
    const finalData=[];
    if(data.length){
        for (const physician of data){
            finalData.push({
                npi_id: physician?.npi_id,
                name: physician?.first_name + ' ' + physician?.last_name,
                location: physician?.address1 + ', ' + physician?.city + ', ' + physician?.state + ',' + physician?.zip,
                segmentation: Array.isArray(physician?.segmentation) ? (physician?.segmentation.join(', ') || []) : physician?.segmentation,
                district: physician?.district
            });
        }
    }
    return finalData;
    
};



export const { actions } = physicianSlice;
export default physicianSlice.reducer;
